"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useEffect, useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import { cn } from "@/lib/utils";
import type { StatCardProps } from "./StatCard.props";
import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/**
 * Icon size classes by card size.
 */
const ICON_SIZE_MAP = {
  normal: "w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16",
  large: "w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24"
} as const;

/**
 * Value text size classes by card size.
 */
const VALUE_SIZE_MAP = {
  normal: "text-3xl md:text-4xl lg:text-5xl mb-3",
  large: "text-5xl md:text-6xl lg:text-7xl mb-4"
} as const;

/**
 * Label text size classes by card size.
 */
const LABEL_SIZE_MAP = {
  normal: "text-base md:text-lg",
  large: "text-lg md:text-xl"
} as const;
type LucideIconComponent = React.ComponentType<{
  className?: string;
  strokeWidth?: number;
}>;

/**
 * Parses a stat value string to extract prefix, numeric value, suffix, and decimal count.
 * Handles formats like "95%", "$2.4M", "10x", "150+", "3,500", etc.
 */
function parseStatValue(value: string): {
  prefix: string;
  number: number | null;
  suffix: string;
  decimals: number;
} {
  // Strip commas so "3,500" parses as 3500
  const stripped = value.replace(/,/g, "");
  const match = stripped.match(/^([^0-9]*)([0-9]+(?:\.[0-9]+)?)(.*)$/);
  if (!match || !match[2]) return {
    prefix: "",
    number: null,
    suffix: value,
    decimals: 0
  };
  const decimals = match[2].includes(".") ? match[2].split(".")[1]?.length ?? 0 : 0;
  return {
    prefix: match[1] ?? "",
    number: parseFloat(match[2]),
    suffix: match[3] ?? "",
    decimals
  };
}

/**
 * Formats a number back to the original style, preserving commas for values ≥ 1000.
 */
function formatNumber(value: number, decimals: number, originalHadCommas: boolean): string {
  if (decimals > 0) return value.toFixed(decimals);
  const rounded = Math.round(value);
  if (originalHadCommas && rounded >= 1000) {
    return rounded.toLocaleString("en-US");
  }
  return rounded.toString();
}

/**
 * StatCard molecule.
 *
 * Renders a single statistic with animated entrance, optional icon,
 * and source attribution. Numeric values count up from zero when
 * scrolled into view, making stats feel earned and credible.
 * Designed for use inside PortableStatistics or any statistics display section.
 */
export function StatCard(t0) {
  const $ = _c(46);
  const {
    stat,
    isDark: t1,
    size: t2,
    className
  } = t0;
  const isDark = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "normal" : t2;
  const cardRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const el = cardRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(t5 => {
        const [entry] = t5;
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      }, {
        rootMargin: "-50px"
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  const rawValue = stat.value ?? "";
  let t5;
  if ($[2] !== rawValue) {
    t5 = rawValue.includes(",");
    $[2] = rawValue;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const hadCommas = t5;
  let t6;
  if ($[4] !== rawValue) {
    t6 = parseStatValue(rawValue);
    $[4] = rawValue;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const parsed = t6;
  const [displayValue, setDisplayValue] = useState(rawValue);
  let t7;
  let t8;
  if ($[6] !== hadCommas || $[7] !== isInView || $[8] !== parsed) {
    t7 = () => {
      if (!isInView || parsed.number === null) {
        return;
      }
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reducedMotion) {
        return;
      }
      let frameId;
      const startTime = performance.now();
      const target = parsed.number;
      const tick = now => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / 1500, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const latest = eased * target;
        const formatted = formatNumber(latest, parsed.decimals, hadCommas);
        setDisplayValue(`${parsed.prefix}${formatted}${parsed.suffix}`);
        if (progress < 1) {
          frameId = requestAnimationFrame(tick);
        }
      };
      frameId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frameId);
    };
    t8 = [isInView, parsed, hadCommas];
    $[6] = hadCommas;
    $[7] = isInView;
    $[8] = parsed;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  const SourceWrapper = stat.sourceUrl ? "a" : "span";
  let t9;
  if ($[11] !== isDark || $[12] !== stat.source || $[13] !== stat.sourceUrl) {
    t9 = stat.sourceUrl ? {
      href: stat.sourceUrl,
      target: "_blank" as const,
      rel: "noopener noreferrer",
      "aria-label": stat.source || "Source",
      className: cn("transition-colors duration-300", isDark ? "hover:text-overlay-accent" : "hover:text-primary")
    } : {};
    $[11] = isDark;
    $[12] = stat.source;
    $[13] = stat.sourceUrl;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const sourceProps = t9;
  const IconComponent = stat.icon ? (LucideIcons as unknown as Record<string, LucideIconComponent>)[stat.icon] : null;
  const t10 = isDark ? "bg-overlay text-overlay-foreground border border-white/10" : "bg-card text-card-foreground border border-border";
  const t11 = IconComponent ? "flex-row items-start gap-6" : "flex-col";
  let t12;
  if ($[15] !== className || $[16] !== t10 || $[17] !== t11) {
    t12 = cn("flex h-full", "rounded-2xl p-6 md:p-8", t10, "backdrop-blur-sm shadow-lg", "transition-all duration-300", t11, className);
    $[15] = className;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== IconComponent || $[20] !== size) {
    t13 = IconComponent && <Box className="flex-shrink-0 opacity-60"><IconComponent className={ICON_SIZE_MAP[size]} strokeWidth={1.5} /></Box>;
    $[19] = IconComponent;
    $[20] = size;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const t14 = isDark ? "text-overlay-accent" : "text-primary";
  const t15 = VALUE_SIZE_MAP[size];
  let t16;
  if ($[22] !== t14 || $[23] !== t15) {
    t16 = cn("font-bold tracking-tight", t14, t15);
    $[22] = t14;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== displayValue || $[26] !== t16) {
    t17 = <Box className={t16}>{displayValue}</Box>;
    $[25] = displayValue;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = LABEL_SIZE_MAP[size];
  let t19;
  if ($[28] !== t18) {
    t19 = cn("leading-relaxed flex-grow opacity-80", t18);
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== stat.label || $[31] !== t19) {
    t20 = <Text as="p" className={t19}>{stat.label}</Text>;
    $[30] = stat.label;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== SourceWrapper || $[34] !== isDark || $[35] !== sourceProps || $[36] !== stat.source) {
    t21 = stat.source && <Box className={cn("mt-4 pt-4 border-t flex items-center gap-2", isDark ? "border-white/10" : "border-border")}><Box className="w-4 h-px bg-primary/40" /><SourceWrapper {...sourceProps}><cite className={cn("not-italic text-xs font-semibold uppercase tracking-wider", isDark ? "text-overlay-foreground/70" : "text-card-foreground/70")}>{stat.source}</cite></SourceWrapper></Box>;
    $[33] = SourceWrapper;
    $[34] = isDark;
    $[35] = sourceProps;
    $[36] = stat.source;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t17 || $[39] !== t20 || $[40] !== t21) {
    t22 = <Box className="flex flex-col flex-grow">{t17}{t20}{t21}</Box>;
    $[38] = t17;
    $[39] = t20;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t12 || $[43] !== t13 || $[44] !== t22) {
    t23 = <Box ref={cardRef} className={t12}>{t13}{t22}</Box>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  return t23;
}